import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/utils';

const toastVariants = cva(
  'group relative w-full overflow-hidden rounded-md border bg-background text-foreground shadow',
  {
    variants: {
      variant: {
        default: '',
        destructive:
          'border-destructive bg-destructive text-destructive-foreground',
        success:
          'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-foreground',
        warning:
          'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-foreground',
        info: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-foreground',
      },
      size: {
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export type ToastProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof toastVariants>;

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ className, variant, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(toastVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Toast.displayName = 'Toast';

export const ToastTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm font-semibold', className)}
    {...props}
  />
));
ToastTitle.displayName = 'ToastTitle';

export const ToastDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('mt-1 text-sm text-muted-foreground', className)}
    {...props}
  />
));
ToastDescription.displayName = 'ToastDescription';

export const ToastClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'absolute right-1 top-1 rounded p-1 text-muted-foreground/70 transition hover:text-foreground focus:outline-none',
      className,
    )}
    aria-label="Dismiss"
    {...props}
  >
    ×
  </button>
));
ToastClose.displayName = 'ToastClose';
